import { useEffect, useState } from 'react';
import { useUserStore } from '@/stores/userStore';
import { resolveAssetUrl } from '@/shop/assetUrl';
import { CoinIcon } from '@/components/CurrencyIcon';
import { audio } from '@/audio/AudioService';
import { useT } from '@/i18n/useI18n';
import { adsAvailable, showRewarded } from '@/ads/adService';
import { RARITY_COLOR } from '@/characters/characterApi';
import { useHoldSkinStore } from '@/holdskins/holdSkinStore';
import type { HoldSkinEntry } from '@/holdskins/holdSkinsApi';
import { HoldShapePreview } from '@/holdskins/HoldShapePreview';

/**
 * Hold-skin store section embedded in the Shop screen. Players BUY wall-grip
 * shapes here; equipping happens in the Inventory (BAG). Hold skins are a
 * client-local cosmetic — equipping one overrides the per-match random pick.
 */
export function HoldSkinsShopSection() {
  const t = useT();
  const setProfile = useUserStore((s) => s.setProfile);
  const items = useHoldSkinStore((s) => s.items);
  const coins = useHoldSkinStore((s) => s.coins);
  const gems = useHoldSkinStore((s) => s.gems);
  const loaded = useHoldSkinStore((s) => s.loaded);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);

  useEffect(() => { void useHoldSkinStore.getState().refresh(); }, []);

  // Keep the wallet pills on the Shop header in sync with what the store knows.
  useEffect(() => {
    if (loaded) setProfile({ coins, gems });
  }, [coins, gems, loaded, setProfile]);

  function flash(kind: 'ok' | 'err', text: string) {
    setToast({ text, kind });
    window.setTimeout(() => setToast(null), 2200);
  }

  async function buy(it: HoldSkinEntry, currency: 'coins' | 'gems') {
    setBusyId(it.id);
    try {
      await useHoldSkinStore.getState().buy(it.id, currency);
      audio.play('ui.shop_purchase');
      flash('ok', t('holds.unlocked', { name: it.name }));
    } catch (e: any) {
      audio.play('ui.error');
      flash('err', e?.message ?? t('holds.purchase_failed'));
    }
    setBusyId(null);
  }

  async function startTrial(it: HoldSkinEntry) {
    setBusyId(it.id);
    try {
      const earned = await showRewarded();
      if (!earned) { setBusyId(null); return; }
      const { trialApi } = await import('@/shop/trialApi');
      const r = await trialApi.start('holdSkin', it.id);
      audio.play('ui.shop_purchase');
      await useHoldSkinStore.getState().refresh(); // so trialUsesLeft is set → TRY hides
      flash('ok', t('shop.trial_started', { name: it.name, n: r.usesLeft }));
    } catch (e: any) {
      audio.play('ui.error');
      flash('err', e?.message ?? t('holds.purchase_failed'));
    }
    setBusyId(null);
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="tag" style={{ padding: '8px 2px 8px', color: 'var(--c-purple)' }}>
        {t('holds.shop_section')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!loaded ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: 11, padding: 12, textAlign: 'center' }}>
            {t('holds.loading')}
          </div>
        ) : items.map((it) => (
          <ShopHoldCard
            key={it.id}
            item={it}
            busy={busyId === it.id}
            canCoins={coins >= it.priceCoins}
            canGems={gems >= it.priceGems}
            onBuy={(cur) => buy(it, cur)}
            onTrial={() => startTrial(it)}
          />
        ))}
      </div>
      {toast && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 12,
            textAlign: 'center',
            background: toast.kind === 'ok' ? 'rgba(34,211,238,0.18)' : 'rgba(230,57,70,0.18)',
            border: `1px solid ${toast.kind === 'ok' ? 'rgba(34,211,238,0.6)' : 'rgba(230,57,70,0.6)'}`,
            color: toast.kind === 'ok' ? 'var(--c-cyan)' : 'var(--c-red)',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

function ShopHoldCard({
  item, busy, canCoins, canGems, onBuy, onTrial,
}: {
  item: HoldSkinEntry;
  busy: boolean;
  canCoins: boolean;
  canGems: boolean;
  onBuy: (cur: 'coins' | 'gems') => void;
  onTrial: () => void;
}) {
  const t = useT();
  const accent = RARITY_COLOR[item.rarity];
  const preview = resolveAssetUrl(item.imageUrl);
  return (
    <div
      className="card"
      style={{
        padding: 12, display: 'flex', gap: 12, alignItems: 'center',
        borderColor: item.owned ? `${accent}40` : `${accent}55`,
        opacity: item.owned ? 0.85 : 1,
      }}
    >
      <HoldShapePreview url={preview} accent={accent} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="display" style={{ fontSize: 14, color: accent }}>{item.name}</span>
          <span style={{ fontSize: 8, color: accent, border: `1px solid ${accent}`, padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase' }}>
            {item.rarity}
          </span>
        </div>
        {item.description && (
          <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 3 }}>{item.description}</div>
        )}

        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {item.owned ? (
            <span className="display" style={{ fontSize: 11, color: 'var(--c-cyan)', alignSelf: 'center' }}>
              {t('holds.owned_equip')}
            </span>
          ) : (
            <>
              {item.priceCoins > 0 && (
                <button
                  disabled={busy || !canCoins}
                  onClick={() => onBuy('coins')}
                  className="btn"
                  style={{
                    fontSize: 11, padding: '7px 12px',
                    background: 'rgba(244,199,36,0.15)', border: '1px solid var(--c-yellow)',
                    color: canCoins ? 'var(--c-yellow)' : 'var(--ink-dim)',
                  }}
                >
                  <CoinIcon size={13} /> {item.priceCoins}
                </button>
              )}
              {item.priceGems > 0 && (
                <button
                  disabled={busy || !canGems}
                  onClick={() => onBuy('gems')}
                  className="btn"
                  style={{
                    fontSize: 11, padding: '7px 12px',
                    background: 'rgba(155,93,229,0.15)', border: '1px solid var(--c-purple)',
                    color: canGems ? 'var(--c-purple)' : 'var(--ink-dim)',
                  }}
                >
                  💎 {item.priceGems}
                </button>
              )}
              {adsAvailable() && item.trialUsesLeft == null && (item.priceCoins > 0 || item.priceGems > 0) && (
                <button
                  disabled={busy}
                  onClick={onTrial}
                  className="btn"
                  style={{
                    fontSize: 11, padding: '7px 12px',
                    background: 'rgba(34,211,238,0.12)', border: '1px dashed var(--c-cyan)',
                    color: 'var(--c-cyan)',
                  }}
                >
                  📺 {t('shop.trial')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
